import type {
  AgentApiCandidateProfilesParseDocument,
  SharedCustomField,
} from '@/types/serializers';
import type { CandidateProfilePostRequest } from '@/features/agent/candidates/types/CandidateProfile';
import { buildCustomDataPayload, type CustomDataFormValues } from '@/utils/customDataForm';

/**
 * 書類解析結果を RHF 形式の customData レコードに変換する純粋関数。
 *
 * CandidateProfileAddForm の handleDocumentParsed と同じ変換ロジックを踏襲する:
 * - multiple_select: datum.content（カンマ区切りの value 文字列）を
 *   選択肢 ID 配列（string[]）に変換する
 * - それ以外: datum.content をそのまま設定する
 */
export function buildCustomDataRecordFromParse(
  result: Pick<AgentApiCandidateProfilesParseDocument, 'custom_data'>,
  customFields: SharedCustomField[] | undefined
): CustomDataFormValues {
  // カスタムフィールドを ID でルックアップできる Map を作成する
  const fieldsById = new Map((customFields ?? []).map((field) => [field.id, field]));

  // RHF 形式の customData レコードを構築する（キー: field_id 文字列、値: string | string[]）
  const customData: CustomDataFormValues = {};

  for (const datum of result.custom_data) {
    const field = fieldsById.get(datum.field_id);

    if (field?.field_type === 'multiple_select') {
      // multiple_select: カンマ区切りの value 文字列を選択肢 ID 配列に変換する
      const values = datum.content
        .split(',')
        .map((v) => v.trim())
        .filter((v) => v !== '');
      const optionIds = field.custom_field_options
        .filter((option) => values.includes(option.value))
        .map((option) => String(option.id));
      customData[String(datum.field_id)] = optionIds;
    } else {
      // その他（text / number / date / single_select 等）: content をそのまま使用する
      customData[String(datum.field_id)] = datum.content;
    }
  }

  return customData;
}

/**
 * 複数の customData レコードをマージする純粋関数。
 *
 * 先勝ちルール: 先頭のレコードが優先され、後続レコードはそのキーが未設定（undefined）のときのみ補完する。
 * 例として [履歴書のcustomData, 職歴書のcustomData] の順で渡すことで履歴書優先になる。
 */
export function mergeCustomDataRecords(records: CustomDataFormValues[]): CustomDataFormValues {
  const merged: CustomDataFormValues = {};

  for (const record of records) {
    for (const [key, value] of Object.entries(record)) {
      // 既にキーが存在する場合はスキップ（先勝ちルール）
      if (!(key in merged)) {
        merged[key] = value;
      }
    }
  }

  return merged;
}

/**
 * 書類解析結果を候補者新規作成 API のペイロードに変換する純粋関数。
 * （後方互換のために維持。内部で buildCustomDataRecordFromParse を使用する）
 *
 * CandidateProfileAddForm の handleDocumentParsed と同じ変換ロジックを踏襲する:
 * - multiple_select: datum.content（カンマ区切りの value 文字列）を
 *   選択肢 ID 配列（string[]）に変換する
 * - それ以外: datum.content をそのまま設定する
 */
export function buildCandidateCreatePayloadFromParse(
  result: AgentApiCandidateProfilesParseDocument,
  customFields: SharedCustomField[] | undefined,
  fallbackName = '無題の求職者'
): CandidateProfilePostRequest {
  // parse結果を RHF 形式のカスタムデータレコードに変換する
  const customData = buildCustomDataRecordFromParse(result, customFields);

  // customData を API ペイロード形式（{field_id, content}[]）に変換する
  const customDataPayload = buildCustomDataPayload(customData, 'create');

  // 氏名が抽出できなかった場合は仮氏名を使用する
  const name = result.name?.trim() ? result.name.trim() : fallbackName;

  return {
    name,
    custom_data: customDataPayload.length > 0 ? customDataPayload : undefined,
    // 解析済みファイルを document_files としてアタッチするために blob signed_id を送信する
    document_blob_signed_ids: [result.blob_signed_id],
  };
}
